import { useEffect, useState, useCallback } from "react";
import { Timer, Plus } from "lucide-react";
import { api } from "../lib/api";

/**
 * Header pill showing the current minute balance.
 * Polls /me/wallet every 60s so it stays roughly in sync after calls.
 * Hidden for stealth admins (they have an unlimited pool).
 */
export const MinuteBalance = ({ onTopUp }) => {
  const [wallet, setWallet] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/wallet");
      setWallet(data);
    } catch (e) { /* unauth or network */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    // Allow other components (e.g. RealtimeCall, ChatSession) to push a refresh
    // after a paid voice call ends.
    window.addEventListener("wallet-refresh", load);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("wallet-refresh", load);
    };
  }, [load]);

  if (!wallet) return null;
  // Unlimited / stealth admin pool — hide the badge to keep the UI clean.
  if (wallet.minutes_balance > 9000) return null;

  const minutes = wallet.minutes_balance;
  const isLow = wallet.is_low_balance;
  const isNegative = wallet.is_negative;

  const tone = isNegative
    ? "border-red-400/60 text-red-300 bg-red-400/10"
    : isLow
      ? "border-amber-400/60 text-amber-300 bg-amber-400/10"
      : "border-novatus-green/50 text-novatus-green bg-novatus-green/10";

  return (
    <button
      onClick={onTopUp}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border ${tone} text-xs font-bold transition-colors hover:bg-white/5`}
      data-testid="minute-balance-pill"
      title={isNegative ? "Guthaben aufgebraucht — bitte nachladen" : "Verbleibende Voice-Minuten"}
    >
      <Timer className="w-3.5 h-3.5" />
      <span data-testid="minute-balance-value">{minutes.toFixed(1)}<span className="hidden sm:inline"> Min</span></span>
      {(isLow || isNegative) && <Plus className="w-3 h-3 opacity-70" />}
    </button>
  );
};

export default MinuteBalance;
